import { Link, useLocation } from "react-router-dom";
import { Home, Users, Briefcase, User, MessageSquare } from "lucide-react";
import { useState, useEffect } from "react";

export default function Navbar() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navLinks = [
    { name: "Home", path: "/", icon: Home, highlight: true },
    { name: "About", path: "/about", icon: Users },
    { name: "Practice", path: "/practice-areas", icon: Briefcase },
    { name: "Our commitment", path: "/team", icon: User },
    { name: "Contact Us", path: "/contact", icon: MessageSquare, highlight: true },
  ];

  return (
    <>
      {/* ================= DESKTOP NAVBAR ================= */}
      <nav
        className={`hidden lg:block sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-slate-900/95 backdrop-blur-md shadow-lg border-b border-slate-800"
            : "bg-slate-900 border-b border-slate-800"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-28">
            {/* LOGO */}
            <Link
              to="/"
              onClick={scrollToTop}
              className="flex items-center gap-5 group flex-shrink-0"
            >
              <img
                src="/assets/logo.png"
                alt="BlackThorn Chambers Logo"
                className="
                  h-28 xl:h-32 w-auto object-contain mix-blend-screen
                  transition-all duration-300
                  group-hover:scale-105
                  group-hover:drop-shadow-[0_0_14px_rgba(234,179,8,0.45)]
                "
              />
              <div>
                <h1 className="text-white text-3xl xl:text-4xl font-semibold whitespace-nowrap">
                  Blackthorn Chambers
                </h1>
                <p className="text-sm xl:text-base uppercase tracking-widest text-yellow-400 whitespace-nowrap">
                  Advocates & Legal Consultants
                </p>
              </div>
            </Link>

            {/* RIGHT SIDE */}
            <div className="flex items-center gap-6 flex-shrink-0">
              <ul className="flex items-center gap-2 flex-nowrap">
                {navLinks.map((item) => {
                  const isActive = pathname === item.path;

                  return (
                    <li key={item.name} className="relative flex-shrink-0">
                      <Link
                        to={item.path}
                        onClick={scrollToTop}
                        className={`
                          px-4 py-2 rounded-lg transition-all duration-300 whitespace-nowrap
                          ${
                            item.highlight
                              ? "text-lg font-bold"
                              : "text-base font-semibold"
                          }
                          ${
                            isActive
                              ? "text-yellow-400"
                              : "text-slate-300 hover:text-white"
                          }
                          ${
                            item.highlight &&
                            "hover:scale-105 hover:drop-shadow-[0_0_8px_rgba(234,179,8,0.45)]"
                          }
                        `}
                      >
                        {item.name}
                      </Link>

                      {isActive && (
                        <span
                          className={`
                            absolute left-1/2 -translate-x-1/2 bottom-0 h-0.5 bg-yellow-400 rounded-full
                            ${item.highlight ? "w-3/4" : "w-1/2"}
                          `}
                        />
                      )}
                    </li>
                  );
                })}

                {/* PRIVACY POLICY */}
                <li className="relative flex-shrink-0">
                  <Link
                    to="/privacy-policy"
                    onClick={scrollToTop}
                    className={`px-4 py-2 text-base font-semibold transition ${
                      pathname === "/privacy-policy"
                        ? "text-yellow-400"
                        : "text-slate-300 hover:text-white"
                    }`}
                  >
                    Privacy Policy
                  </Link>

                  {pathname === "/privacy-policy" && (
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-0 h-0.5 bg-yellow-400 w-1/2 rounded-full" />
                  )}
                </li>
              </ul>

              {/* DISCLAIMER */}
              <Link
                to="/disclaimer"
                onClick={scrollToTop}
                className={`px-4 py-2 rounded-lg border-2 font-semibold transition-all duration-300 ${
                  pathname === "/disclaimer"
                    ? "border-yellow-400 text-yellow-400"
                    : "border-slate-600 text-slate-300 hover:border-yellow-400 hover:text-yellow-400"
                } hover:scale-105`}
              >
                Disclaimer
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* ================= MOBILE TOP HEADER ================= */}
      <div className="lg:hidden bg-slate-900 border-b border-slate-800">
        <div className="flex items-center px-4 py-4">
          <Link to="/" onClick={scrollToTop} className="flex items-center gap-4">
            <img
              src="/assets/logo.png"
              alt="BlackThorn Chambers Logo"
              className="h-24 w-auto object-contain mix-blend-screen"
            />
            <div>
              <h1 className="text-white text-2xl font-bold">
                Blackthorn Chambers
              </h1>
              <p className="text-xs uppercase tracking-widest text-yellow-400">
                Advocates & Legal Consultants
              </p>
            </div>
          </Link>
        </div>
      </div>

      {/* ================= MOBILE BOTTOM NAV ================= */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-slate-900/95 backdrop-blur-md border-t border-slate-800">
        <div className="grid grid-cols-5 h-16">
          {navLinks.map((item) => {
            const isActive = pathname === item.path;
            const Icon = item.icon;

            return (
              <Link
                key={item.name}
                to={item.path}
                onClick={scrollToTop}
                className={`flex flex-col items-center justify-center gap-1 ${
                  isActive ? "text-yellow-400" : "text-slate-400"
                }`}
              >
                <Icon size={22} strokeWidth={isActive ? 2.5 : 2} />
                <span className={item.highlight ? "text-xs font-bold" : "text-[10px]"}>
                  {item.name}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}
